import React, { useEffect, useRef, useState, useMemo } from 'react';
import { createPortal } from 'react-dom';
import { AnimatePresence, motion } from 'framer-motion';
import { useMachine } from '@xstate/react';

import { ToastMessage } from '@chargify/custom-ui-components';
import { useDeviceInfo } from '@chargify/device-provider';

import { toastMessageMotion } from './motion';
import { Container } from './ToastsContainer.styles';

import { ToastMessagesContext } from '../../contexts';
import { toasts } from '../../machines';
import { setNotificationsPosition } from '../../utils';

import { ToastsService } from '../../types';

type ToastsContainerProps = {
  /**
   * React children nodes
   */
  children: React.ReactNode;
};

const ToastsContainer = ({ children }: ToastsContainerProps) => {
  const el = useRef(null);
  const [isMounted, setMounted] = useState(false);

  const [state, send] = useMachine(toasts);
  const { device } = useDeviceInfo();

  useEffect(() => {
    el.current = document.createElement('div');
    el.current.setAttribute('data-testid', 'toast-notifications-portal');
    el.current.style.position = 'fixed';
    el.current.style.pointerEvents = 'none';
    el.current.style.zIndex = '2';

    document.body.appendChild(el.current);
    setMounted(true);

    return () => {
      el.current.remove();
    };
  }, []);

  useEffect(() => {
    if (el.current) {
      setNotificationsPosition(el.current, device);
    }
  }, [device]);

  const toastService: ToastsService = useMemo(
    () => ({
      show: ({ message, level, hideAfterTimeMs }) => {
        send('SHOW_TOAST', {
          message,
          level,
          hideAfterTimeMs,
        });
      },
    }),
    []
  );

  const toastNotifications =
    device === 'mobile'
      ? [...state.context.items].reverse()
      : state.context.items;

  return (
    <ToastMessagesContext.Provider value={{ service: toastService }}>
      {children}
      {isMounted
        ? createPortal(
            <Container centerNotifications={device === 'mobile'}>
              <AnimatePresence>
                {toastNotifications.map(({ id, message, level }) => (
                  <motion.div {...toastMessageMotion} key={id} layout>
                    <ToastMessage type={level} message={message} />
                  </motion.div>
                ))}
              </AnimatePresence>
            </Container>,
            el.current
          )
        : null}
    </ToastMessagesContext.Provider>
  );
};

export default ToastsContainer;
